/**
 * The enrollment key SECRET: the app reads it only through the router tool `network_key_read`,
 * never from PostgREST (app#379, router#1754, docs/PERMISSIONS.md § 5 Network: "Read … an
 * enrollment key — network owner or Admin").
 *
 * Pure, so `enrollmentKey.test.ts` can check it with `node --experimental-strip-types`. The
 * components pass `callTool` in; nothing here imports the tools client or the Supabase client.
 *
 * ⚠⚠ ROUTER MIGRATION 0216 WITHHOLDS `tokens.secret` FROM EVERY APP CALLER, an Owner or Admin
 * included. After it, a SELECT that names `secret` fails as a whole with 42501, so the key list,
 * Add Node and the side menu's Add Node button would all break. Nothing in the app may select,
 * return or send that column again: `TOKEN_SELECT` below is the one column list.
 */

/**
 * The `tokens` columns the app reads through PostgREST. ⚠ NEVER add `secret` (see above). The
 * realtime channel carries only these columns too, because Realtime sends only what the role may
 * SELECT.
 */
export const TOKEN_SELECT = 'id, created_at, used_at, name, enabled, network_id, digest' as const

/** The payload of `network_key_read`, after `callTool` unwraps it. */
export interface NetworkKeyRead {
  token_id: string
  name: string
  network_id: string
  network_name: string
  enabled: boolean
  secret: string
}

/** What a read of a key's secret came to. */
export type KeySecretResult =
  | { kind: 'secret'; secret: string }
  /** The reader is not the network's owner or an org Owner/Admin. Hide the key. */
  | { kind: 'forbidden' }
  | { kind: 'error'; message: string }

type ToolCall = <T>(name: string, args: Record<string, unknown>) => Promise<T>

/** The sentence a reader who may not read the key gets, where the key would be. */
export const KEY_FORBIDDEN =
  'Only the owner of this network, or an owner or admin of its organization, can see its enrollment key.'

/**
 * The reason on the greyed Delete Key button, for a reader who does not control the network
 * (app#422). The router refuses the delete to the same readers (`network_key_delete`).
 */
export const KEY_DELETE_FORBIDDEN =
  'Only the owner of this network, or an owner or admin of its organization, can delete its enrollment key.'

/**
 * Reads the secret of the key `tokenId` through `network_key_read`.
 *
 * ⚠ The router writes an audit row (`network.key_read`) for each call, so call this when the key
 * is shown, copied or put in an install command, not when a page loads.
 * ⚠ A refusal is a RESULT, never a throw: `forbidden` hides the key; any other failure is one
 * sentence. The secret never goes to the console.
 */
export async function readKeySecret(call: ToolCall, tokenId: string): Promise<KeySecretResult> {
  try {
    const key = await call<NetworkKeyRead>('network_key_read', { token_id: tokenId })
    if (!key?.secret) return { kind: 'error', message: 'The router returned no key.' }
    return { kind: 'secret', secret: key.secret }
  } catch (e) {
    const failure = e as { code?: string; message?: string } | null
    if (failure?.code === 'forbidden') return { kind: 'forbidden' }
    console.error('network_key_read failed:', failure?.code, failure?.message)
    if (failure?.code === 'unauthenticated' || failure?.code === 'unauthorized') {
      return { kind: 'error', message: 'Your session has ended. Sign in again, then try once more.' }
    }
    return { kind: 'error', message: 'Could not read the key. Try again.' }
  }
}

/** What the Add Node install command is, for one reader and one selected key. */
export interface InstallChoice {
  /** The signed-in enrollment: the installer with no key. */
  keyless: boolean
  /** The key to put in the command. Null in keyless mode and while the read is not done. */
  secret: string | null
}

/**
 * Decides the Add Node install command (app#379). `mayReadKey` is the UI's guess that the router
 * will give this reader the key (network owner or org Owner/Admin). `read` is the last answer of
 * `network_key_read` and the key it was for.
 *
 * ⚠ A reader who may not read the key gets the keyless command at once, with no read. A manager
 * waits for the read of THIS key (keyless false, secret null: the block is disabled), and a read
 * that gave no secret falls back to the keyless command, which works for any member.
 * ⚠ An answer for another key is never used: the reader may have changed the network since.
 */
export function installChoice(
  mayReadKey: boolean,
  tokenId: string | undefined,
  read: { tokenId: string; secret: string | null } | null
): InstallChoice {
  if (!mayReadKey) return { keyless: true, secret: null }
  if (!tokenId || !read || read.tokenId !== tokenId) return { keyless: false, secret: null }
  return read.secret ? { keyless: false, secret: read.secret } : { keyless: true, secret: null }
}
